import { Minus, Plus } from 'lucide-react';
import { useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { User } from '../../service/api';

interface StickersManagementModalProps {
  show: boolean;
  onHide: () => void;
  user: User | null;
  currentBalance: number;
  onConfirm: (amount: number) => Promise<void>;
}

export default function StickersManagementModal({
  show,
  onHide,
  user,
  currentBalance,
  onConfirm,
}: StickersManagementModalProps) {
  const { t } = useTranslation();
  const [amount, setAmount] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);

  const handleDecrease = () => {
    setAmount(prev => prev - 1);
  };

  const handleIncrease = () => {
    setAmount(prev => prev + 1);
  };

  const handleConfirm = async () => {
    if (amount === 0) return;

    setIsProcessing(true);
    try {
      await onConfirm(amount);
      setAmount(0);
    } catch (error) {
      console.error('Failed to update stickers:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClose = () => {
    if (!isProcessing) {
      setAmount(0);
      onHide();
    }
  };

  const resultingBalance = currentBalance + amount;
  const isValid = amount >= 0 || resultingBalance >= 0;

  if (!user) return null;

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>{t('manager.users.manageStickersTitle')}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="text-center mb-4">
          <h5>
            {user.given_name} {user.family_name}
          </h5>
          <p className="text-muted mb-0">{user.email}</p>
        </div>

        <div className="mb-4">
          <div className="text-center mb-3">
            <span className="text-muted">
              {t('manager.users.currentBalance')}:{' '}
            </span>
            <span className="fw-bold">{currentBalance}</span>
          </div>

          <div className="d-flex align-items-center justify-content-center gap-3">
            <Button
              variant="outline-danger"
              size="lg"
              className="rounded-circle"
              style={{ width: '64px', height: '64px' }}
              onClick={handleDecrease}
              disabled={isProcessing}
            >
              <Minus size={24} />
            </Button>

            <div
              className={`fs-1 fw-bold text-center ${
                amount > 0 ? 'text-success' : amount < 0 ? 'text-danger' : ''
              }`}
              style={{ minWidth: '100px' }}
            >
              {amount > 0 ? '+' : ''}
              {amount}
            </div>

            <Button
              variant="outline-success"
              size="lg"
              className="rounded-circle"
              style={{ width: '64px', height: '64px' }}
              onClick={handleIncrease}
              disabled={isProcessing}
            >
              <Plus size={24} />
            </Button>
          </div>

          {!isValid && (
            <div className="text-danger text-center mt-3">
              {t('manager.users.insufficientBalance')}
            </div>
          )}

          <div className="text-center mt-3">
            <span className="text-muted">
              {t('manager.users.resultingBalance')}:{' '}
            </span>
            <span className={`fw-bold ${!isValid ? 'text-danger' : ''}`}>
              {resultingBalance}
            </span>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="secondary"
          onClick={handleClose}
          disabled={isProcessing}
        >
          {t('common.cancel')}
        </Button>
        <Button
          variant="primary"
          onClick={handleConfirm}
          disabled={isProcessing || amount === 0 || !isValid}
        >
          {t('common.confirm')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
